import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Form,
    Button,
    Message,
    Radio,
    Input,
    Segment,
    Header,
    Table,
    Dimmer,
    Loader,
    Icon,
    Popup,
    Grid,
    Label
} from 'semantic-ui-react';
import { useTextResources, useMessageResources } from '../../language';
import { Invoice } from '../../api/services/next';
import { onError, onSuccess, onWarning } from '../shared/messages';
import { useLoader } from '../../hooks/useLoader';
import moment from 'moment';
import { dateFormat } from '../shared/constants';
import formatCurrency from 'format-currency';
import { Currencies } from '../shared/currencies';
import { CreateStornoInvoice } from '@/api/services/finance';
import './styles.css';
import { RenderTemplateWithReCalcTax } from '@/api/services/finance';
import saveAs from 'file-saver';

/**
 * RefusedInvoiceTreatmentPopup - React Modal for handling refused tax invoices
 * 
 * Displays treatment options for invoices with tax authority errors:
 * - Option 1: Cancel Invoice (Storno)
 * - Option 2: Issue Without Allocation Number
 * - Option 3: Reverse & Self-Invoice (Without VAT)
 * - Option 4: Initiate Hearing Process
 * 
 * Can be opened from Angular using the controller prop:
 * window.RefusedInvoiceTreatmentPopupController('open', { invoice, taxAuthorityError, onSuccess })
 */
const RefusedInvoiceTreatmentPopup = ({
    open: startOpen,
    onClose,
    invoice: initInvoice,
    taxAuthorityError: initTaxAuthorityError,
    onSuccess: onSuccessCallback,
    controller,
    ...rest
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();

    // State - initialized from props
    const [open, setOpen] = useState(startOpen);
    const [invoice, setInvoice] = useState(initInvoice);
    const [taxAuthorityError, setTaxAuthorityError] = useState(initTaxAuthorityError);
    const [params, setParams] = useState(null);

    // Form state
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [approverName, setApproverName] = useState('');
    const [approverRole, setApproverRole] = useState('');
    const [error, setError] = useState(null);

    // Sync with prop changes
    useEffect(() => {
        setInvoice(initInvoice);
        setTaxAuthorityError(initTaxAuthorityError);
        setOpen(startOpen);
    }, [initInvoice, initTaxAuthorityError, startOpen]);

    // Angular controller integration - allows opening from Angular
    useEffect(() => {
        if (controller) {
            window[controller] = (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setInvoice(params.invoice);
                            setTaxAuthorityError(params.taxAuthorityError);
                            setParams(params);
                            // Reset form state
                            setSelectedOption(null);
                            setApproverName('');
                            setApproverRole('');
                            setError(null);
                        }
                        setOpen(true);
                        break;
                    default:
                        break;
                }
            };
        }
    }, []);

    // Status display mapping
    const statusTexts = useMemo(() => ({
        1: text('Open'),
        2: text('Pending'),
        3: text('Closed'),
        4: text('TechnicalError')
    }), [text]);

    // Currency formatting
    const currencyFormat = useCallback((currencyId) => {
        return {
            format: "%v%s",
            symbol: Currencies[currencyId]?.symbol || '',
            locale: document.documentElement.lang || "he-IL",
        };
    }, []);

    // Format amount for display
    const formatAmount = useCallback((amount, currencyId) => {
        if (amount == null) return '-';
        return formatCurrency(amount, currencyFormat(currencyId || 1));
    }, [currencyFormat]);

    // Validation - check if form is valid
    const isValid = useCallback(() => {
        if (!selectedOption) return false;
        // Option 3 requires approver details
        if (selectedOption === 3 && (!approverName.trim() || !approverRole.trim())) {
            return false;
        }
        return true;
    }, [selectedOption, approverName, approverRole]);

    // Handle option selection
    const handleOptionChange = useCallback((e, { value }) => {
        setSelectedOption(value);
        setError(null);
        // Clear approver fields if not option 3
        if (value !== 3) {
            setApproverName('');
            setApproverRole('');
        }
    }, []);

    // Handle confirm - execute selected treatment
    const handleConfirm = async () => {
        if (!isValid()) {
            setError(message('PleaseSelectTreatmentOption'));
            return;
        }

        setLoading(true);
        loader(true);
        setError(null);

        try {
            let result;

            switch (selectedOption) {
                case 1:
                    // Option 1: Cancel Invoice - Create storno and mark as treated
                    result = await Invoice.ProcessRefusedInvoiceTreatment({
                        InvoiceId: invoice.Id,
                        TreatmentOption: 1,
                        VatNumber: invoice.HotelVATNumber
                    });
                    if (!result || !result.Success) {
                        onError(result?.Message || message('TaxAuthorityAPIReturnedErrors'));
                        return;
                    }
                    await CreateStornoInvoice({
                        CurrencyID: invoice.CurrencyID,
                        FolioID: invoice.FolioID,
                        StornoInvoiceID: invoice.Id,
                        ReCreateDebitTransactions: true,
                        TemplateID: invoice.TemplateID
                    });
                    //Remove pending tag
                    await Invoice.RemoveTaxAuthorityPendingTag(invoice.FolioID)
                    break;

                case 2:
                    // Option 2: Issue Without Allocation Number
                    result = await Invoice.ProcessRefusedInvoiceTreatment({
                        InvoiceId: invoice.Id,
                        TreatmentOption: 2,
                        VatNumber: invoice.HotelVATNumber
                    });
                    if (!result || !result.Success) {
                        onError(result?.Message || message('TaxAuthorityAPIReturnedErrors'));
                        return;
                    }
                    await createInvoiceTemplate(invoice.Id);
                    //Remove pending tag
                    await Invoice.RemoveTaxAuthorityPendingTag(invoice.FolioID)
                    break;

                case 3:
                    // Option 3: Reverse & Self-Invoice (Without VAT)
                    result = await Invoice.ProcessRefusedInvoiceTreatment({
                        InvoiceId: invoice.Id,
                        TreatmentOption: 3,
                        VatNumber: invoice.HotelVATNumber,
                        ApproverName: approverName.trim(),
                        ApproverRole: approverRole.trim()
                    });
                    if (!result || !result.Success) {
                        onError(result?.Message || message('TaxAuthorityAPIReturnedErrors'));
                        return;
                    }
                    await CreateStornoInvoice({
                        CurrencyID: invoice.CurrencyID,
                        FolioID: invoice.FolioID,
                        StornoInvoiceID: invoice.Id,
                        ReCreateDebitTransactions: true,
                        TemplateID: invoice.TemplateID,
                        IsForTaxExemptReversal: true,
                        OriginalInvoiceIdAllocationRejected: invoice.Id,
                    });
                    //Remove pending tag
                    await Invoice.RemoveTaxAuthorityPendingTag(invoice.FolioID)
                    break;

                case 4:
                    // Option 4: Initiate Hearing
                    result = await Invoice.ProcessRefusedInvoiceTreatment({
                        InvoiceId: invoice.Id,
                        TreatmentOption: 4,
                        VatNumber: invoice.HotelVATNumber
                    });
                    if (!result || !result.Success) {
                        onError(result?.Message || message('TaxAuthorityAPIReturnedErrors'));
                        return;
                    }
                    break;

                default:
                    onError(message('InvalidTreatmentOption'));
                    return;
            }
            onSuccess();

            // Call callback from props or from params (Angular)
            if (onSuccessCallback) {
                onSuccessCallback(result);
            }
            if (params && params.onSuccess) {
                params.onSuccess(result);
            }

            handleClose();
        } catch (err) {
            console.error('Treatment execution failed:', err);
            setError(err.message || err.Message || message('TaxAuthorityAPIReturnedErrors'));
            onError(err);
        } finally {
            setLoading(false);
            loader(false);
        }
    };

    const createInvoiceTemplate = async (invoiceId) => {
            try {

                const invoice = await Invoice.GetInvoice({ Id: invoiceId })

                const files = []

                const paramters = [
                    { key: "CurrencyId", value: invoice.CurrencyId?.toString() },
                    { key: "FolioId", value: invoice.FolioId?.toString() },
                    { key: "HotelId", value: invoice.HotelId?.toString() },
                    { key: "InvoiceId", value: invoice.Id?.toString() },
                    { key: "DeductionNote", value: true }
                ]

                const copy = await RenderTemplateWithReCalcTax({
                    IsPrintable: true,
                    TemplateID: invoice.TemplateId,
                    HotelID: invoice.HotelId,
                    Paramters: [
                        ...paramters,
                        { key: "InvoiceTitle", value: "CopyTaxInvoice" }
                    ]
                })

                files.push(copy.PdfDto.DmzUrlPathAccessibleByBrowser)

                const original = await RenderTemplateWithReCalcTax({
                    IsPrintable: true,
                    TemplateID: invoice.TemplateId,
                    HotelID: invoice.HotelId,
                    Paramters: [
                        ...paramters,
                        { key: "InvoiceTitle", value: "OriginalTaxInvoice" }
                    ]
                })
                files.push(original.PdfDto.DmzUrlPathAccessibleByBrowser)

                files.forEach(file => {
                    saveAs(file)
                })
            }
            catch (error) {
                onError(error)
            }
    }

    // Handle cancel/close
    const handleClose = useCallback(() => {
        // Reset state
        setSelectedOption(null);
        setApproverName('');
        setApproverRole('');
        setError(null);
        setLoading(false);
        setOpen(false);

        if (onClose) {
            onClose();
        }
    }, [onClose]);

    // Get error details from taxAuthorityError
    const errorDetails = useMemo(() => {
        if (!taxAuthorityError) return null;

        // Handle both single object and array (TaxAuthorityInvoiceErrors collection)
        const errorObj = Array.isArray(taxAuthorityError)
            ? taxAuthorityError[0]
            : taxAuthorityError;

        return errorObj;
    }, [taxAuthorityError]);

    // Parse error message from JSON format like: [{"code":422,"message":"..."}]
    const parsedErrorMessage = useMemo(() => {
        const errorStr = errorDetails?.ErrorDescription || errorDetails?.Errors || '';
        if (!errorStr) return null;

        try {
            // Try to parse as JSON
            let parsed = errorStr;
            if (typeof errorStr === 'string') {
                parsed = JSON.parse(errorStr);
            }

            // Handle array format [{"code":422,"message":"..."}]
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed[0].message || parsed[0].Message || errorStr;
            }

            // Handle object format {"code":422,"message":"..."}
            if (parsed && typeof parsed === 'object') {
                return parsed.message || parsed.Message || errorStr;
            }

            return errorStr;
        } catch (e) {
            // If not valid JSON, return as is
            return errorStr;
        }
    }, [errorDetails]);

    // State for expanding error message
    const [isErrorExpanded, setIsErrorExpanded] = useState(false);
    const MAX_ERROR_LENGTH = 70;

    // Determine if error needs truncation
    const errorNeedsTruncation = parsedErrorMessage && parsedErrorMessage.length > MAX_ERROR_LENGTH;
    const displayedError = errorNeedsTruncation && !isErrorExpanded
        ? parsedErrorMessage.substring(0, MAX_ERROR_LENGTH) + '...'
        : parsedErrorMessage;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="small"
            closeIcon
            closeOnDimmerClick={false}
            closeOnEscape={!loading}
            {...rest}
        >
            <Modal.Header>
                {text('RefusedInvoiceTreatment')}
            </Modal.Header>
            <Modal.Content scrolling>
                {/* Compact Invoice Information Section */}
                <Segment className="invoice-info-compact">
                    <Header as="h4" dividing>
                        <Icon name="file text outline" />
                        {text('InvoiceDetails')}
                    </Header>
                    <div className="invoice-info-inline">
                        <span><strong>{text('InvoiceNumber')}:</strong> {invoice?.InvoiceNumber || '-'}</span>
                        <span><strong>{text('InvoiceDate')}:</strong> {invoice?.CreateDate ? moment(invoice.CreateDate).format(dateFormat) : '-'}</span>
                        <span><strong>{text('Status')}:</strong> {statusTexts[errorDetails?.InvoiceStatus] || '-'}</span>
                    </div>
                    <div className="error-info-inline" style={{ marginTop: 8, color: '#d9534f' }}>
                        <strong>{text('Error')}:</strong>{' '}
                        <span style={{ fontWeight: 'bold' }}>{errorDetails?.ErrorCode || '-'}</span>
                        {parsedErrorMessage && (
                            <>
                                <span> - </span>
                                {errorNeedsTruncation ? (
                                    <Popup
                                        content={parsedErrorMessage}
                                        trigger={<span className="error-message-text">{displayedError}</span>}
                                        wide="very"
                                        position="top left"
                                    />
                                ) : (
                                    <span>{parsedErrorMessage}</span>
                                )}
                            </>
                        )}
                    </div>
                </Segment>

                {/* Treatment Options Section */}
                <Segment>
                    <Header as="h4" dividing>
                        <Icon name="options" />
                        {text('SelectTreatmentOption')}
                    </Header>
                    <Form>
                        {/* Option 1: Cancel Invoice */}
                        <Form.Field className="treatment-option">
                            <Radio
                                label={text('CancelInvoice')}
                                name="treatmentOption"
                                value={1}
                                checked={selectedOption === 1}
                                onChange={handleOptionChange}
                                disabled={loading}
                            />
                        </Form.Field>

                        {/* Option 2: Issue Without Allocation */}
                        <Form.Field className="treatment-option">
                            <Radio
                                label={text('IssueWithoutAllocation')}
                                name="treatmentOption"
                                value={2}
                                checked={selectedOption === 2}
                                onChange={handleOptionChange}
                                disabled={loading}
                            />
                        </Form.Field>                        {/* Option 3: Reverse & Self-Invoice - Currently disabled, no API available in Interface Manager */}
                        <Form.Field className="treatment-option">
                                        <Radio
                                            label={text('ReverseAndSelfInvoice')}
                                            name="treatmentOption"
                                            value={3}
                                            checked={selectedOption === 3}
                                            onChange={handleOptionChange}
                                            disabled={true}
                            />
                        </Form.Field>

                        {/* Option 3 Additional Fields - Approver Details */}
                        {selectedOption === 3 && (
                            <Segment secondary className="approver-fields">
                                <Header as="h5">
                                    <Icon name="user" />
                                    {text('ApproverDetails')}
                                </Header>
                                <Form.Field required>
                                    <label>{text('ApproverName')}</label>
                                    <Input
                                        value={approverName}
                                        onChange={(e) => setApproverName(e.target.value)}
                                        placeholder={text('ApproverName')}
                                        disabled={loading}
                                        error={selectedOption === 3 && !approverName.trim()}
                                    />
                                </Form.Field>
                                <Form.Field required>
                                    <label>{text('ApproverRole')}</label>
                                    <Input
                                        value={approverRole}
                                        onChange={(e) => setApproverRole(e.target.value)}
                                        placeholder={text('ApproverRole')}
                                        disabled={loading}
                                        error={selectedOption === 3 && !approverRole.trim()}
                                    />
                                </Form.Field>
                                <Message warning visible size="small">
                                    <Icon name="warning sign" />
                                    {message('ThisOptionWillCreateTaxExemptTransactions')}
                                </Message>
                            </Segment>
                        )}

                        {/* Option 4: Initiate Hearing */}
                        <Form.Field className="treatment-option">
                            <Radio
                                label={text('InitiateHearing')}
                                name="treatmentOption"
                                value={4}
                                checked={selectedOption === 4}
                                onChange={handleOptionChange}
                                disabled={loading}
                            />
                        </Form.Field>
                    </Form>
                </Segment>

                {/* Error Message */}
                {error && (
                    <Message negative>
                        <Message.Header>{text('Error')}</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    negative
                    size="small"
                    onClick={handleClose}
                    disabled={loading}
                >
                    {text('Cancel')}
                </Button>
                <Button
                    positive
                    size="small"
                    onClick={handleConfirm}
                    disabled={!isValid() || loading}
                    loading={loading}
                >
                    <Icon name="check" />
                    {text('Confirm')}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

RefusedInvoiceTreatmentPopup.propTypes = {
    /** Controls popup visibility */
    open: PropTypes.bool,
    /** Called when popup closes */
    onClose: PropTypes.func,
    /** The invoice object being treated */
    invoice: PropTypes.object,
    /** Tax authority error details. Can be single object or array */
    taxAuthorityError: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    /** Called after successful treatment with result data */
    onSuccess: PropTypes.func,
    /** Controller name for Angular integration - exposes window[controller] function */
    controller: PropTypes.any
};

export default RefusedInvoiceTreatmentPopup;
